import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import ConfirmDialog from '../ConfirmDialog';
import { FileText, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  RENEWAL_LABELS,
  getDocTypesForProperty,
  getGroupedDocTypes,
} from '../../lib/docTypes';
import DocumentDetail from './DocumentDetail';
import AIReviewDialog from './AIReviewDialog';
import {
  iconFor,
  getDocumentIcon,
  getDocumentLabel,
  getDocumentStatus,
  calculateDaysUntilExpiry,
  formatDate,
  API,
} from './utils';

export default function DocumentsSection({ property, documents, propertyId, onDocumentsChanged, openDocumentRequest }) {
  const location = useLocation();
  const [selectedDocTypes, setSelectedDocTypes] = useState([]);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [showAddDocType, setShowAddDocType] = useState(false);
  const [customDocType, setCustomDocType] = useState('');
  const [setExpiryFor, setSetExpiryFor] = useState(null);
  const [setExpiryDate, setSetExpiryDate] = useState('');
  const [docTypeToRemove, setDocTypeToRemove] = useState(null);
  const [removeDocTypeDialogOpen, setRemoveDocTypeDialogOpen] = useState(false);
  const [analyzingDocId, setAnalyzingDocId] = useState(null);
  const [aiReview, setAiReview] = useState(null); // { docId, docType, fields }

  // Rebuild visible doc-type slots whenever property/documents change.
  // Defaults: Mandatory always + HMO additional when property type is HMO.
  // Merge with existing user-selected slots and any types already uploaded so
  // legacy docs and previous selections survive a refetch.
  useEffect(() => {
    if (!property) return;
    const existingTypes = documents.map(d => d.document_type);
    const defaultsForType = getDocTypesForProperty(property.property_type)
      .filter(t => t.category === 'mandatory' || (t.category === 'hmo' && property.property_type === 'HMO'))
      .map(t => t.value);
    setSelectedDocTypes(prev => [...new Set([...defaultsForType, ...prev, ...existingTypes])]);
  }, [property, documents]);

  // Auto-open a specific document when navigated here with location.state.openDocument.
  useEffect(() => {
    if (location.state?.openDocument && documents.length > 0) {
      setExpandedDoc(location.state.openDocument);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, documents]);

  // Imperative open via parent (e.g. PropertyDetail urgent-banner click). The
  // signal is a string + a stamp; the stamp guarantees re-trigger even when
  // the user clicks the same doc type twice in a row.
  useEffect(() => {
    if (!openDocumentRequest?.docType) return;
    setExpandedDoc(openDocumentRequest.docType);
    // Give React one tick to render, then scroll the expanded panel into view.
    setTimeout(() => {
      const node = document.querySelector('.documents-section');
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, [openDocumentRequest]);

  const triggerAiAnalysis = async (docId, docType) => {
    setAnalyzingDocId(docId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/documents/${docId}/analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fields = response.data || {};
      const hasAny = Object.values(fields).some(v => v !== null && v !== undefined && v !== '');
      if (hasAny) {
        setAiReview({ docId, docType, fields });
      } else {
        toast.info('AI did not find any extractable details — please fill in manually.');
      }
    } catch (err) {
      console.error('AI analysis failed:', err);
      toast.error('AI extraction unavailable — you can still edit the document manually.');
    } finally {
      setAnalyzingDocId(null);
    }
  };

  const handleUploadDocument = async (docType, file, expiryDate) => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    if (expiryDate) formData.append('expiry_date', expiryDate);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(`${API}/properties/${propertyId}/documents`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded — analysing with AI…');
      await onDocumentsChanged();
      const newDocId = resp.data?.id;
      if (newDocId) triggerAiAnalysis(newDocId, docType);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted');
      await onDocumentsChanged();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleReplaceDocument = async (docType, file, expiryDate, oldDocId) => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    if (expiryDate) formData.append('expiry_date', expiryDate);
    try {
      const token = localStorage.getItem('token');
      // Upload new first; only delete old after the new is safely stored
      const resp = await axios.post(`${API}/properties/${propertyId}/documents`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (oldDocId) {
        try {
          await axios.delete(`${API}/documents/${oldDocId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (_) { /* refresh will reflect actual state */ }
      }
      toast.success('Document replaced successfully');
      await onDocumentsChanged();
      const newDocId = resp.data?.id;
      if (newDocId) triggerAiAnalysis(newDocId, docType);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to replace document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleUpdateDocument = async (docId, fields) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/documents/${docId}`, fields, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document updated');
      await onDocumentsChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    }
  };

  const handleSaveExpiryOnly = async () => {
    if (!setExpiryFor || !setExpiryDate) {
      toast.error('Please pick a date');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('document_type', setExpiryFor);
      fd.append('expiry_date', setExpiryDate);
      await axios.post(`${API}/properties/${propertyId}/documents/expiry-only`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expiry date set');
      setSetExpiryFor(null);
      setSetExpiryDate('');
      await onDocumentsChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set expiry');
    }
  };

  const addDocumentType = (typeValue) => {
    if (!selectedDocTypes.includes(typeValue)) {
      setSelectedDocTypes([...selectedDocTypes, typeValue]);
    }
    setShowAddDocType(false);
    setExpandedDoc(typeValue);
  };

  const removeDocumentType = (typeValue) => {
    const hasDoc = documents.some(d => d.document_type === typeValue);
    if (hasDoc) {
      setDocTypeToRemove(typeValue);
      setRemoveDocTypeDialogOpen(true);
      return;
    }
    setSelectedDocTypes(selectedDocTypes.filter(t => t !== typeValue));
    if (expandedDoc === typeValue) setExpandedDoc(null);
  };

  const confirmRemoveDocType = async () => {
    const doc = documents.find(d => d.document_type === docTypeToRemove);
    if (doc) await handleDeleteDocument(doc.id);
    setSelectedDocTypes(selectedDocTypes.filter(t => t !== docTypeToRemove));
    if (expandedDoc === docTypeToRemove) setExpandedDoc(null);
    setRemoveDocTypeDialogOpen(false);
    setDocTypeToRemove(null);
  };

  const expandedDocObj = expandedDoc ? documents.find(d => d.document_type === expandedDoc) : null;

  return (
    <>
      <div className="documents-section">
        <div className="section-header-with-action">
          <h3 className="section-title-simple">
            <FileText size={20} />
            Documents
          </h3>
          <Button onClick={() => setShowAddDocType(true)} className="btn-add-doc-type">
            <Plus size={18} />
            Add Document Type
          </Button>
        </div>

        <div className="documents-layout">
          <div className="documents-sidebar">
            {selectedDocTypes.map((typeValue) => {
              const doc = documents.find(d => d.document_type === typeValue);
              const hasFile = !!doc?.cloudinary_url;
              const hasExpiry = !!doc?.expiry_date;
              const status = doc ? getDocumentStatus(doc.expiry_date) : 'missing';
              const days = doc ? calculateDaysUntilExpiry(doc.expiry_date) : 0;
              const Icon = getDocumentIcon(typeValue);
              const isSelected = expandedDoc === typeValue;

              return (
                <div
                  key={typeValue}
                  className={`document-sidebar-item ${isSelected ? 'selected' : ''} status-${status}`}
                  data-testid={`document-sidebar-${typeValue}`}
                >
                  <div className="doc-sidebar-clickable" onClick={() => setExpandedDoc(typeValue)}>
                    <div className="doc-sidebar-icon">
                      <Icon size={20} />
                    </div>
                    <div className="doc-sidebar-content">
                      <div className="doc-sidebar-title">{getDocumentLabel(typeValue)}</div>
                      {doc && hasExpiry && days !== null && (
                        <div className={`doc-sidebar-badge status-${status}`}>
                          {days < 0 ? `${Math.abs(days)} overdue` : `${days} days`}
                        </div>
                      )}
                      {doc && !hasFile && hasExpiry && (
                        <div className="doc-sidebar-status doc-sidebar-status-pending">Expiry set · no file</div>
                      )}
                      {!doc && (
                        <div className="doc-sidebar-empty-row">
                          <span className="doc-sidebar-status">Not uploaded</span>
                          <button
                            type="button"
                            className="doc-set-expiry-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSetExpiryFor(typeValue);
                              setSetExpiryDate('');
                            }}
                            data-testid={`set-expiry-link-${typeValue}`}
                          >
                            Set expiry date
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDocumentType(typeValue);
                    }}
                    className="btn-remove-doc-type"
                    title={doc ? "Remove document and type" : "Remove document type"}
                  >
                    <X size={14} />
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="documents-detail">
            {expandedDoc ? (
              <DocumentDetail
                typeValue={expandedDoc}
                typeLabel={getDocumentLabel(expandedDoc)}
                icon={getDocumentIcon(expandedDoc)}
                doc={expandedDocObj}
                status={expandedDocObj ? getDocumentStatus(expandedDocObj.expiry_date) : 'missing'}
                days={expandedDocObj ? calculateDaysUntilExpiry(expandedDocObj.expiry_date) : 0}
                formatDate={formatDate}
                propertyId={propertyId}
                onUpload={handleUploadDocument}
                onReplace={handleReplaceDocument}
                onUpdate={handleUpdateDocument}
                onDelete={handleDeleteDocument}
                onChanged={onDocumentsChanged}
                uploading={uploadingDoc === expandedDoc}
                analyzing={analyzingDocId !== null && expandedDocObj?.id === analyzingDocId}
              />
            ) : (
              <div className="documents-detail-empty">
                <FileText size={64} className="empty-icon" />
                <p>Select a document to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showAddDocType} onOpenChange={setShowAddDocType}>
        <DialogContent className="add-doc-type-dialog">
          <DialogHeader>
            <DialogTitle>Add Document Type</DialogTitle>
            <DialogDescription>
              Pick a compliance document to track on this property. The slot will appear in your sidebar — you can upload the file or set an expiry-only reminder.
            </DialogDescription>
          </DialogHeader>
          <div className="doc-type-list">
            {getGroupedDocTypes(property?.property_type).map(group => {
              const items = group.items.filter(t => !selectedDocTypes.includes(t.value));
              if (items.length === 0) return null;
              return (
                <div key={group.key} className="doc-type-group" data-testid={`doc-type-group-${group.key}`}>
                  <div className="doc-type-group-label">{group.label}</div>
                  {items.map((type) => {
                    const Icon = iconFor(type.iconName);
                    return (
                      <div
                        key={type.value}
                        className="doc-type-option"
                        onClick={() => addDocumentType(type.value)}
                        data-testid={`doc-type-option-${type.value}`}
                      >
                        <Icon size={20} />
                        <div className="doc-type-option-text">
                          <span>{type.label}</span>
                          <span className="doc-type-option-sub">{RENEWAL_LABELS[type.renewal]}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <div className="doc-type-custom">
              <Input
                placeholder="Other (custom document type)"
                value={customDocType}
                onChange={(e) => setCustomDocType(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && customDocType.trim()) {
                    addDocumentType(customDocType.trim().toLowerCase().replace(/\s+/g, '_'));
                    setCustomDocType('');
                  }
                }}
                data-testid="doc-type-custom-input"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!setExpiryFor} onOpenChange={(open) => { if (!open) { setSetExpiryFor(null); setSetExpiryDate(''); } }}>
        <DialogContent className="set-expiry-dialog" data-testid="set-expiry-dialog">
          <DialogHeader>
            <DialogTitle>Set expiry date</DialogTitle>
            <DialogDescription>
              Record a renewal date without uploading the file yet. The document will count toward your renewals and dashboard alerts.
            </DialogDescription>
          </DialogHeader>
          <p className="ai-review-hint" style={{ marginBottom: 16 }}>
            Set just the expiry date for <strong>{setExpiryFor && getDocumentLabel(setExpiryFor)}</strong>. It will appear on the Renewals page and count toward your dashboard totals. You can upload the actual file later.
          </p>
          <div className="form-group">
            <Label htmlFor="set-expiry-input">Expiry date</Label>
            <Input
              id="set-expiry-input"
              type="date"
              value={setExpiryDate}
              onChange={(e) => setSetExpiryDate(e.target.value)}
              data-testid="set-expiry-input"
            />
          </div>
          <div className="ai-review-actions">
            <Button onClick={() => { setSetExpiryFor(null); setSetExpiryDate(''); }} className="btn-secondary" data-testid="set-expiry-cancel">
              Cancel
            </Button>
            <Button onClick={handleSaveExpiryOnly} className="btn-primary" data-testid="set-expiry-save">
              Save expiry
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeDocTypeDialogOpen}
        onOpenChange={setRemoveDocTypeDialogOpen}
        title="Remove Document Type"
        description={`Are you sure you want to remove this document type? ${documents.some(d => d.document_type === docTypeToRemove) ? 'This will also delete the uploaded document.' : 'This action cannot be undone.'}`}
        onConfirm={confirmRemoveDocType}
        confirmText="Remove"
      />

      <AIReviewDialog
        review={aiReview}
        propertyId={propertyId}
        property={property}
        onClose={() => setAiReview(null)}
        onApplied={async () => {
          setAiReview(null);
          await onDocumentsChanged();
        }}
      />
    </>
  );
}
